import type { SocialMensajesConversacion, SocialMensajesPanorama } from "../../../types";

export const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
export const MESES_LARGO = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
export const DIAS_CORTO = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const DIA_MS = 86_400_000;

export function edadDias(iso: string | null, ahora: Date = new Date()): number {
  if (!iso) return 0;
  return Math.max(0, Math.floor((ahora.getTime() - new Date(iso).getTime()) / DIA_MS));
}

export function textoEdad(dias: number): string {
  if (dias <= 1) return "hoy o ayer";
  if (dias < 31) return `hace ${dias} días`;
  if (dias < 365) return `hace ${Math.round(dias / 30)} meses`;
  return `hace ${(dias / 365).toFixed(1).replace(".", ",")} años`;
}

export type EstadoConversacion = "responder" | "vencida" | "respondida";

export function estadoDe(
  c: Pick<SocialMensajesConversacion, "sin_respuesta" | "ultimo_mensaje_ts">,
  diasParaResponder: number,
  ahora: Date = new Date(),
): EstadoConversacion {
  if (!c.sin_respuesta) return "respondida";
  return edadDias(c.ultimo_mensaje_ts, ahora) <= diasParaResponder ? "responder" : "vencida";
}

/** Páginas visibles: primera, última, la actual y sus vecinas, con "…" en los saltos. */
export function paginasVisibles(actual: number, total: number): Array<number | "…"> {
  const base = [...new Set([1, total, actual, actual - 1, actual + 1])].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const salida: Array<number | "…"> = [];
  base.forEach((n, i) => {
    if (i > 0 && n - base[i - 1] > 1) salida.push("…");
    salida.push(n);
  });
  return salida;
}

export interface MesPunto {
  mes: string;
  conversaciones: number;
  sinRespuesta: number;
}

/** Los últimos `n` meses hasta `ahora`, con 0 en los meses sin conversaciones nuevas. */
export function serieMensual(mensual: SocialMensajesPanorama["mensual"], n: number, ahora: Date = new Date()): MesPunto[] {
  const porMes = new Map(mensual.map((m) => [m.mes, m]));
  const salida: MesPunto[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
    const mes = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const f = porMes.get(mes);
    salida.push({ mes, conversaciones: f?.conversaciones ?? 0, sinRespuesta: f?.sin_respuesta ?? 0 });
  }
  return salida;
}

/** Matriz [lunes..domingo][0..23] a partir de BigQuery, donde DAYOFWEEK vale 1 para domingo. */
export function matrizHoras(horas: SocialMensajesPanorama["horas"]): number[][] {
  const m = Array.from({ length: 7 }, () => Array<number>(24).fill(0));
  for (const h of horas) {
    const fila = (h.dow + 5) % 7; // 2 (lunes) -> 0 ... 1 (domingo) -> 6
    if (fila >= 0 && fila < 7 && h.hora >= 0 && h.hora < 24) m[fila][h.hora] += h.mensajes;
  }
  return m;
}

/** Ventana de 3 horas seguidas con más mensajes, como [desde, hasta). */
export function picoHoras(matriz: number[][]): { desde: number; hasta: number } | null {
  const porHora = Array.from({ length: 24 }, (_, h) => matriz.reduce((a, fila) => a + fila[h], 0));
  if (porHora.every((v) => v === 0)) return null;
  let mejor = 0;
  let suma = -1;
  for (let h = 0; h <= 21; h++) {
    const s = porHora[h] + porHora[h + 1] + porHora[h + 2];
    if (s > suma) {
      suma = s;
      mejor = h;
    }
  }
  return { desde: mejor, hasta: mejor + 3 };
}

/** Nombre del tramo i según los límites en días que define el servidor (7,30,90,365 → «Hasta 7 días», «7 a 30 días», …, «Más de 365 días»). */
export function etiquetaTramo(i: number, tramos: number[]): string {
  if (i === 0) return `Hasta ${tramos[0]} días`;
  if (i >= tramos.length) return `Más de ${tramos[tramos.length - 1]} días`;
  return `${tramos[i - 1]} a ${tramos[i]} días`;
}
